import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Card, DesignV2Scope, Stepper, StickyBottomActions } from "../design-v2";
import {
  GeneratedProgramResult,
  GenerationProgress,
  StackHeader,
  WeekdaySelector,
} from "../components/product";
import { useManualProgram } from "../context/ManualProgramContext";
import SettingsDrawer from "../features/settings/SettingsDrawer";
import { SETTINGS_SCREEN_IDS } from "../features/settings/settingsNavigation";
import { isAIWeeklyPlanFrontendEnabled } from "../features/weeklyPlans/featureFlags";
import { resolveBackTarget } from "../features/weeklyPlans/navigation";
import {
  getManualBuilderPath,
  getWeeklyPlanDetailsPath,
  getWeeklyPlansPath,
} from "../features/weeklyPlans/routes";
import { createGenerationId } from "../features/generation/generationId";
import {
  GENERATION_RESULT_UNCERTAIN_MESSAGE,
  isGenerationTransportLoss,
} from "../features/generation/generationRecovery";
import { buildGenerationProfileChips } from "../features/onboarding/profileSummary";
import useOnboardingGenerationProgress from "../features/onboarding/useOnboardingGenerationProgress";
import {
  DAY_OF_WEEK_OPTIONS,
  adjustTrainingDaysForSessions,
  areSameTrainingDays,
  getSpacedDefaultTrainingDays,
  isValidPreferredTrainingDays,
  normalizeTrainingDays,
  resolvePreferredTrainingDays,
} from "../features/onboarding/trainingDayDefaults";
import {
  createAIWeeklyPlanDraft,
  getUserSettings,
  openOrCreateWeeklyPlanEditDraft,
  updateTrainingProfileAvailability,
} from "../services/api";
import "./aiWeeklyPlanBuilder.css";

const EMPTY_AVAILABILITY_OPTIONS = Object.freeze({});
const EMPTY_AVAILABILITY_VALUES = Object.freeze([]);
const TRAINING_PROFILE_INITIAL_SCREEN = Object.freeze({
  id: SETTINGS_SCREEN_IDS.TRAINING_PROFILE_MENU,
});

const PROFILE_ERROR_MESSAGES = {
  PROFILE_NOT_READY:
    "Complete your Training Profile before generating a program.",
  UNSUPPORTED_PROFILE_SCHEMA_VERSION:
    "Your Training Profile uses a version that is not yet supported. Open it to update your profile.",
  EMPTY_EXERCISE_POOL:
    "No compatible exercises were found for your Training Profile. Review your profile and try again.",
  AI_WEEKLY_PLAN_UNSUPPORTED_PRIMARY_GOAL:
    "Your primary goal is not yet supported by the AI Builder. Review your Training Profile to continue.",
};

const RETRYABLE_ERROR_CODES = new Set([
  "AI_WEEKLY_PLAN_GENERATION_TIMEOUT",
  "AI_WEEKLY_PLAN_PROVIDER_UNAVAILABLE",
  "AI_WEEKLY_PLAN_MODEL_UNAVAILABLE",
  "AI_WEEKLY_PLAN_PROVIDER_RATE_LIMITED",
  "AI_WEEKLY_PLAN_INVALID_PROVIDER_RESPONSE",
  "AI_WEEKLY_PLAN_INVALID_OUTPUT",
  "AI_WEEKLY_PLAN_REPAIR_FAILED",
  // Phase 10B: the durable GenerationAttempt this page now owns resolved
  // without a plan. Retrying replays the same generationId rather than
  // starting a second generation, so both stay retryable.
  "AI_GENERATION_ATTEMPT_FAILED",
  "AI_GENERATION_ATTEMPT_INTERRUPTED",
]);

// The server has closed this generationId for good. Onboarding's proven rule
// applies here too: retrying is allowed, but it must start a NEW logical
// generation instead of replaying a request the backend will never honour.
const TERMINAL_ATTEMPT_MESSAGES = {
  AI_GENERATION_ATTEMPTS_EXHAUSTED:
    "We couldn't finish your program after several tries. Try again to start a new generation.",
  AI_GENERATION_ABANDONED:
    "That program request expired before it could start. Try again to start a new generation.",
};

// One AI generation per user per UTC day. Retrying cannot succeed before the reset, so this
// kind has no "Try again" button (it is not in RETRY_ERROR_KINDS).
const DAILY_LIMIT_MESSAGE =
  "You've already used your AI program generation for today. The limit resets at 00:00 UTC, then you can generate a new program.";

const RETRY_ERROR_KINDS = ["retryable", "save", "availability", "terminal"];

function createAttemptRecoveryError(terminalStatus) {
  const error = new Error(
    terminalStatus === "FAILED"
      ? "We couldn't generate your weekly plan."
      : "Program generation was interrupted."
  );
  error.code = terminalStatus === "FAILED"
    ? "AI_GENERATION_ATTEMPT_FAILED"
    : "AI_GENERATION_ATTEMPT_INTERRUPTED";
  error.status = 409;
  return error;
}

function getPublicGenerationError(error) {
  const code = error?.code || null;

  if (PROFILE_ERROR_MESSAGES[code]) {
    return {
      kind: "profile",
      message: PROFILE_ERROR_MESSAGES[code],
    };
  }

  if (code === "AI_WEEKLY_PLAN_BUILDER_DISABLED") {
    return {
      kind: "disabled",
      message: "The AI Builder is temporarily unavailable.",
    };
  }

  if (code === "AI_GENERATION_DAILY_LIMIT_REACHED") {
    return {
      kind: "limit",
      message: DAILY_LIMIT_MESSAGE,
    };
  }

  if (TERMINAL_ATTEMPT_MESSAGES[code]) {
    return {
      kind: "terminal",
      message: TERMINAL_ATTEMPT_MESSAGES[code],
    };
  }

  if (RETRYABLE_ERROR_CODES.has(code)) {
    return {
      kind: "retryable",
      message: "We couldn't finish generating your program. You can try again.",
    };
  }

  if (error?.status === null || error?.status === undefined) {
    return {
      kind: "network",
      // The generation now carries a stable generationId, so the durable
      // attempt is the source of truth for whether a plan exists. Recovering it
      // is a read, not a second generation.
      message:
        "The connection was interrupted during generation. Your program may still have been created, so check your programs before starting a new generation.",
    };
  }

  return {
    kind: "unknown",
    message: "We can't generate your program right now. Try again later.",
  };
}

function getAvailability(settings) {
  const availability = settings?.trainingProfile?.profile?.availability || {};
  const sessionsPerWeek = availability.sessionsPerWeek ?? null;

  return {
    sessionsPerWeek,
    durationPerSession: availability.durationPerSession ?? null,
    preferredTrainingDays: resolvePreferredTrainingDays(
      availability.preferredTrainingDays,
      sessionsPerWeek
    ),
  };
}

// The AI Builder's generation-affecting local inputs, and nothing else.
function toGenerationInputs(availability) {
  return Object.freeze({
    sessionsPerWeek: availability?.sessionsPerWeek ?? null,
    durationPerSession: availability?.durationPerSession ?? null,
    preferredTrainingDays: Object.freeze(
      normalizeTrainingDays(availability?.preferredTrainingDays)
    ),
  });
}

function areAvailabilityValuesEqual(left, right) {
  return (
    left?.sessionsPerWeek === right?.sessionsPerWeek &&
    left?.durationPerSession === right?.durationPerSession &&
    areSameTrainingDays(
      left?.preferredTrainingDays,
      right?.preferredTrainingDays
    )
  );
}

function getAdjacentValues(allowedValues, currentValue) {
  const values = Array.isArray(allowedValues) ? allowedValues : [];
  const index = values.indexOf(currentValue);

  if (index >= 0) {
    return {
      previous: index > 0 ? values[index - 1] : null,
      next: index < values.length - 1 ? values[index + 1] : null,
      isAllowed: true,
    };
  }

  return {
    previous: values.filter((value) => value < currentValue).at(-1) ?? null,
    next: values.find((value) => value > currentValue) ?? null,
    isAllowed: false,
  };
}

function selectAllowedValue(preferredValue, fallbackValue, allowedValues) {
  const values = Array.isArray(allowedValues) ? allowedValues : [];
  if (values.includes(preferredValue)) return preferredValue;
  if (values.includes(fallbackValue)) return fallbackValue;
  return values[0] ?? fallbackValue ?? preferredValue ?? null;
}

function reconcileAvailabilityAfterSettingsRefresh({
  draftAvailability,
  savedAvailability,
  refreshedAvailability,
  availabilityOptions,
}) {
  if (!draftAvailability || !savedAvailability) {
    return refreshedAvailability;
  }

  const sessionsWereEdited =
    draftAvailability.sessionsPerWeek !== savedAvailability.sessionsPerWeek;
  const durationWasEdited =
    draftAvailability.durationPerSession !== savedAvailability.durationPerSession;
  const daysWereEdited = !areSameTrainingDays(
    draftAvailability.preferredTrainingDays,
    savedAvailability.preferredTrainingDays
  );

  const sessionsPerWeek = selectAllowedValue(
    sessionsWereEdited
      ? draftAvailability.sessionsPerWeek
      : refreshedAvailability.sessionsPerWeek,
    refreshedAvailability.sessionsPerWeek,
    availabilityOptions?.sessionsPerWeek
  );
  const durationPerSession = selectAllowedValue(
    durationWasEdited
      ? draftAvailability.durationPerSession
      : refreshedAvailability.durationPerSession,
    refreshedAvailability.durationPerSession,
    availabilityOptions?.durationPerSession
  );
  const preferredTrainingDays = adjustTrainingDaysForSessions(
    sessionsWereEdited || daysWereEdited
      ? draftAvailability.preferredTrainingDays
      : refreshedAvailability.preferredTrainingDays,
    sessionsPerWeek,
    true
  );

  return {
    sessionsPerWeek,
    durationPerSession,
    preferredTrainingDays,
  };
}

function ProfileSummary({ settingsState, onRetryLoad, onOpenSettings }) {
  if (settingsState.status === "loading") {
    return (
      <Card className="lz-ai-weekly__profile-card" aria-live="polite">
        <h2 id="training-profile-summary-title" className="lz-ai-weekly__card-eyebrow">
          Using your Training Profile
        </h2>
        <div className="lz-ai-weekly__loading-row">
          <span className="lz-ai-weekly__spinner" aria-hidden="true" />
          <span>Loading your Training Profile…</span>
        </div>
      </Card>
    );
  }

  if (settingsState.status === "unavailable") {
    return (
      <Card className="lz-ai-weekly__profile-card">
        <h2 id="training-profile-summary-title" className="lz-ai-weekly__card-eyebrow">
          Using your Training Profile
        </h2>
        <p className="lz-ai-weekly__muted-copy">
          We couldn't load your Training Profile.
        </p>
        <Button type="button" variant="secondary" onClick={onRetryLoad}>
          Try loading again
        </Button>
      </Card>
    );
  }

  const settings = settingsState.data;
  const profile = settings?.trainingProfile?.profile || {};
  const chips = buildGenerationProfileChips(profile);
  const identityItems = chips.filter(({ key }) =>
    ["goal", "experience"].includes(key)
  );
  const contextItems = chips.filter(({ key }) =>
    ["priority", "equipment"].includes(key)
  );

  return (
    <Card className="lz-ai-weekly__profile-card">
      <h2 id="training-profile-summary-title" className="lz-ai-weekly__card-eyebrow">
        Using your Training Profile
      </h2>

      {settings?.meta?.hasTrainingProfile === false ? (
        <div className="lz-ai-weekly__profile-warning">
          <p>
            Your Training Profile is incomplete. Complete it in Settings before
            generating a program.
          </p>
          <Button type="button" variant="secondary" onClick={onOpenSettings}>
            Open Training Profile
          </Button>
        </div>
      ) : null}

      {identityItems.length > 0 ? (
        <p className="lz-ai-weekly__profile-primary">
          {identityItems.map((item) => item.label).join(" · ")}
        </p>
      ) : null}

      {contextItems.length > 0 ? (
        <p className="lz-ai-weekly__profile-secondary">
          {contextItems.map((item) => item.label).join(" · ")}
        </p>
      ) : null}

      <Button
        type="button"
        variant="ghost"
        className="lz-ai-weekly__profile-link"
        onClick={onOpenSettings}
      >
        View Training Profile
      </Button>
    </Card>
  );
}

function AvailabilityControl({
  label,
  description,
  value,
  allowedValues,
  onChange,
  disabled,
  unit,
}) {
  const adjacent = getAdjacentValues(allowedValues, value);

  return (
    <Card className="lz-ai-weekly__availability-card">
      <div className="lz-ai-weekly__control-copy">
        <h3>{label}</h3>
        <p>{description}</p>
      </div>
      <Stepper
        label={label}
        className="lz-ai-weekly__stepper"
        value={value ?? "—"}
        unit={unit}
        onDecrement={() => adjacent.previous != null && onChange(adjacent.previous)}
        onIncrement={() => adjacent.next != null && onChange(adjacent.next)}
        canDecrement={adjacent.previous != null}
        canIncrement={adjacent.next != null}
        disabled={disabled}
        decrementLabel={`Decrease ${label.toLowerCase()}`}
        incrementLabel={`Increase ${label.toLowerCase()}`}
      />
      {!adjacent.isAllowed ? (
        <p className="lz-ai-weekly__field-error">
          Select an available value before generating.
        </p>
      ) : null}
    </Card>
  );
}

// Only the availability save leg still uses a local card. Generation itself is
// owned by the shared, backend-driven GenerationProgress surface.
function AvailabilitySavingState() {
  return (
    <Card
      as="section"
      className="lz-ai-weekly__status-card"
      aria-live="polite"
      aria-busy="true"
    >
      <span className="lz-ai-weekly__spinner lz-ai-weekly__spinner--large" aria-hidden="true" />
      <h2>Saving your availability</h2>
      <p>
        Your program will start generating after your Training Profile is updated.
      </p>
      <Button
        className="lz-ai-weekly__status-button"
        disabled
        aria-label="Saving availability"
      >
        Saving…
      </Button>
    </Card>
  );
}

export default function AIWeeklyPlanBuilder() {
  const navigate = useNavigate();
  const location = useLocation();
  const isEnabled = isAIWeeklyPlanFrontendEnabled();
  const [settingsState, setSettingsState] = useState({
    status: "loading",
    data: null,
  });
  const [savedAvailability, setSavedAvailability] = useState(null);
  const [draftAvailability, setDraftAvailability] = useState(null);
  const [submissionStage, setSubmissionStage] = useState(null);
  const [progressPhase, setProgressPhase] = useState("step");
  const [generationError, setGenerationError] = useState(null);
  const [generationResult, setGenerationResult] = useState(null);
  const [isOpeningDraft, setIsOpeningDraft] = useState(false);
  const [actionError, setActionError] = useState("");
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const { beginHydrationTarget, hydrateProgramDraft } = useManualProgram();
  const mountedRef = useRef(false);
  const inFlightRef = useRef(false);
  const settingsRequestIdRef = useRef(0);
  const savedAvailabilityRef = useRef(null);
  const draftAvailabilityRef = useRef(null);
  const trainingDaysTouchedRef = useRef(false);
  // One logical generation attempt. Held across retries and recovery of the
  // same attempt; released only when the attempt resolves (success) or the
  // server declares it terminal.
  const generationIdRef = useRef(null);
  // Immutable snapshot of the generation-affecting availability the current
  // generationId was started with. Compared, never merged or diffed generically.
  const generationInputsRef = useRef(null);
  const generationRequestControllerRef = useRef(null);
  const pendingResultRef = useRef(null);
  const editInFlightRef = useRef(false);
  const isSubmitting = submissionStage !== null;
  // The one canonical progress implementation, shared with onboarding. The AI
  // Builder only ever drives it through Weekly-Plan phases: it never enters the
  // onboarding-only "checking" / "converting" / "completing" phases, so no
  // Cycle wording can reach this surface.
  const generationProgress = useOnboardingGenerationProgress(
    progressPhase,
    draftAvailability?.sessionsPerWeek,
    draftAvailability?.durationPerSession
  );
  const {
    beginAI,
    markSuccess,
    markWeeklyPlanReady,
    reset: resetGenerationProgress,
    stopAI,
    waitForAICompletion,
  } = generationProgress;
  const availabilityOptions =
    settingsState.data?.trainingProfile?.options?.availability ||
    EMPTY_AVAILABILITY_OPTIONS;
  const sessionsValues =
    availabilityOptions.sessionsPerWeek || EMPTY_AVAILABILITY_VALUES;
  const durationValues =
    availabilityOptions.durationPerSession || EMPTY_AVAILABILITY_VALUES;
  const hasValidAvailability =
    sessionsValues.includes(draftAvailability?.sessionsPerWeek) &&
    durationValues.includes(draftAvailability?.durationPerSession) &&
    isValidPreferredTrainingDays(
      draftAvailability?.preferredTrainingDays,
      draftAvailability?.sessionsPerWeek
    );
  const hasTrainingProfile =
    settingsState.data?.meta?.hasTrainingProfile !== false;
  const canGenerate =
    settingsState.status === "loaded" &&
    hasTrainingProfile &&
    hasValidAvailability &&
    !isSubmitting;
  const availabilityControlsDisabled =
    isSubmitting || settingsState.status !== "loaded";

  const backTarget = useMemo(
    () => resolveBackTarget(location, "/program"),
    [location]
  );

  const setAvailabilityState = useCallback((saved, draft) => {
    savedAvailabilityRef.current = saved;
    draftAvailabilityRef.current = draft;
    setSavedAvailability(saved);
    setDraftAvailability(draft);
  }, []);

  const loadSettings = useCallback(async ({ preserveDraft = false } = {}) => {
    if (!isEnabled) {
      return;
    }

    const requestId = settingsRequestIdRef.current + 1;
    settingsRequestIdRef.current = requestId;

    if (mountedRef.current) {
      setSettingsState({ status: "loading", data: null });
    }

    try {
      const response = await getUserSettings();

      if (mountedRef.current && settingsRequestIdRef.current === requestId) {
        const refreshedAvailability = getAvailability(response);
        const nextAvailability = preserveDraft
          ? reconcileAvailabilityAfterSettingsRefresh({
              draftAvailability: draftAvailabilityRef.current,
              savedAvailability: savedAvailabilityRef.current,
              refreshedAvailability,
              availabilityOptions:
                response?.trainingProfile?.options?.availability ||
                EMPTY_AVAILABILITY_OPTIONS,
            })
          : refreshedAvailability;
        const rawAvailability = response?.trainingProfile?.profile?.availability || {};

        setSettingsState({ status: "loaded", data: response });
        setAvailabilityState(refreshedAvailability, nextAvailability);
        trainingDaysTouchedRef.current = preserveDraft
          ? !areSameTrainingDays(
              nextAvailability.preferredTrainingDays,
              getSpacedDefaultTrainingDays(nextAvailability.sessionsPerWeek)
            )
          : Array.isArray(rawAvailability.preferredTrainingDays) &&
            !areSameTrainingDays(
              rawAvailability.preferredTrainingDays,
              getSpacedDefaultTrainingDays(refreshedAvailability.sessionsPerWeek)
            );
      }
    } catch {
      if (mountedRef.current && settingsRequestIdRef.current === requestId) {
        setSettingsState({ status: "unavailable", data: null });
        if (!preserveDraft) {
          setAvailabilityState(null, null);
        }
      }
    }
  }, [isEnabled, setAvailabilityState]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      settingsRequestIdRef.current += 1;
      // Unmounting must not leave the generation request outstanding. Progress
      // polling is aborted by the shared hook's own effect cleanup.
      generationRequestControllerRef.current?.abort();
      generationRequestControllerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    loadSettings();
  }, [isEnabled, loadSettings]);

  useEffect(() => {
    if (!isSubmitting) {
      return;
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isSubmitting]);

  const handleGenerate = useCallback(async () => {
    if (!isEnabled || inFlightRef.current) {
      return;
    }

    const selectionsAreValid =
      sessionsValues.includes(draftAvailability?.sessionsPerWeek) &&
      durationValues.includes(draftAvailability?.durationPerSession) &&
      isValidPreferredTrainingDays(
        draftAvailability?.preferredTrainingDays,
        draftAvailability?.sessionsPerWeek
      );
    if (!selectionsAreValid || settingsState.status !== "loaded") {
      setGenerationError({
        kind: "availability",
        message:
          "Select available session and duration values and preferred training days before generating.",
      });
      return;
    }

    inFlightRef.current = true;
    setGenerationError(null);
    setGenerationResult(null);
    setActionError("");
    let generationStarted = false;
    let availabilityWasSaved = false;

    try {
      let generationAvailability = draftAvailability;

      if (!areAvailabilityValuesEqual(savedAvailability, draftAvailability)) {
        setSubmissionStage("saving");
        const settingsResponse = await updateTrainingProfileAvailability({
          sessionsPerWeek: draftAvailability.sessionsPerWeek,
          durationPerSession: draftAvailability.durationPerSession,
          preferredTrainingDays: draftAvailability.preferredTrainingDays,
        });
        const authoritativeAvailability = getAvailability(settingsResponse);
        availabilityWasSaved = true;
        generationAvailability = authoritativeAvailability;

        if (mountedRef.current) {
          setSettingsState({ status: "loaded", data: settingsResponse });
          setAvailabilityState(authoritativeAvailability, authoritativeAvailability);
        }
      }

      if (!mountedRef.current) {
        return;
      }

      // Attempt ownership. The id is minted only once the availability save leg
      // has succeeded, so a failed save can never create a backend
      // GenerationAttempt and can never start progress polling.
      //
      // One generationId represents one logical attempt for one stable set of
      // generation-affecting inputs. Retry, response-loss recovery and
      // AI_GENERATION_IN_PROGRESS recovery keep the id while those inputs are
      // unchanged. Changing them after an attempt has started makes the next
      // explicit Generate a NEW logical attempt, so the server can never replay
      // a plan built from availability the user has since abandoned. The
      // previous attempt is simply left alone: nothing cancels or mutates it.
      const generationInputs = toGenerationInputs(generationAvailability);
      const canResumeAttempt = generationIdRef.current !== null &&
        areAvailabilityValuesEqual(generationInputsRef.current, generationInputs);
      const generationId = canResumeAttempt
        ? generationIdRef.current
        : createGenerationId();
      generationIdRef.current = generationId;
      generationInputsRef.current = generationInputs;

      generationStarted = true;
      setSubmissionStage("generating");
      setProgressPhase("generating");
      resetGenerationProgress();
      beginAI(generationId);

      let controller = new AbortController();
      generationRequestControllerRef.current = controller;
      let response;
      try {
        response = await createAIWeeklyPlanDraft({
          generationId,
          signal: controller.signal,
        });
      } catch (error) {
        const transportLoss = isGenerationTransportLoss(error);
        if (error?.code !== "AI_GENERATION_IN_PROGRESS" && !transportLoss) {
          throw error;
        }
        // The server already owns this generationId (or the response was lost after
        // dispatch: proxy timeout, dropped connection). Wait on that authoritative
        // attempt through the shared progress contract instead of starting a
        // second generation, then replay the same id to recover its result.
        const terminalStatus = await waitForAICompletion();
        if (terminalStatus === "UNAVAILABLE" && transportLoss) {
          // Outcome unconfirmed: surfaced as the "network" error; the id is kept.
          throw new Error(GENERATION_RESULT_UNCERTAIN_MESSAGE);
        }
        if (terminalStatus !== "SUCCEEDED") {
          throw createAttemptRecoveryError(terminalStatus);
        }
        if (!mountedRef.current) {
          return;
        }
        controller = new AbortController();
        generationRequestControllerRef.current = controller;
        response = await createAIWeeklyPlanDraft({
          generationId,
          signal: controller.signal,
        });
      }

      generationRequestControllerRef.current = null;
      if (!mountedRef.current) {
        return;
      }
      // The logical attempt is resolved; the next generation is a new one.
      generationIdRef.current = null;
      pendingResultRef.current = response;
      stopAI();
      markWeeklyPlanReady();
      setProgressPhase("success");
      markSuccess();
    } catch (error) {
      generationRequestControllerRef.current = null;
      if (mountedRef.current) {
        stopAI();
        setProgressPhase("step");
        setSubmissionStage(null);
        pendingResultRef.current = null;

        if (!generationStarted) {
          setGenerationError({
            kind: "save",
            message:
              "Your Training Profile was not changed, and generation did not start. Review your selections and try again.",
          });
        } else {
          if (TERMINAL_ATTEMPT_MESSAGES[error?.code]) {
            // The server closed this attempt for good; a retry must start a new
            // logical generation rather than replay a dead id.
            generationIdRef.current = null;
          }
          setGenerationError({
            ...getPublicGenerationError(error),
            availabilityWasSaved,
          });
        }
      }
    } finally {
      inFlightRef.current = false;
    }
  }, [
    beginAI,
    draftAvailability,
    durationValues,
    isEnabled,
    markSuccess,
    markWeeklyPlanReady,
    resetGenerationProgress,
    savedAvailability,
    sessionsValues,
    setAvailabilityState,
    settingsState.status,
    stopAI,
    waitForAICompletion,
  ]);

  // The generated result is revealed only when the shared progress completion
  // state says it is ready, so the accepted success animation runs in full.
  useEffect(() => {
    if (progressPhase !== "success" || !generationProgress.completionReady) {
      return;
    }
    const readyResult = pendingResultRef.current;
    pendingResultRef.current = null;
    setProgressPhase("step");
    setSubmissionStage(null);
    setGenerationResult(readyResult);
  }, [generationProgress.completionReady, progressPhase]);

  const handleSeeDetails = useCallback(() => {
    const weeklyPlanParentId = generationResult?.weeklyPlanParentId;
    if (!weeklyPlanParentId) {
      return;
    }

    navigate(getWeeklyPlanDetailsPath(weeklyPlanParentId), {
      state: { from: backTarget },
    });
  }, [backTarget, generationResult, navigate]);

  const handleModify = useCallback(async () => {
    const weeklyPlanParentId = generationResult?.weeklyPlanParentId;
    if (!weeklyPlanParentId || editInFlightRef.current) {
      return;
    }

    editInFlightRef.current = true;
    setIsOpeningDraft(true);
    setActionError("");

    try {
      await beginHydrationTarget({
        weeklyPlanParentId,
        weeklyPlanVersionId: null,
      });
      const draft = await openOrCreateWeeklyPlanEditDraft(weeklyPlanParentId);
      const detailsPath = getWeeklyPlanDetailsPath(weeklyPlanParentId);
      hydrateProgramDraft(draft, { originRoute: detailsPath });
      navigate(getManualBuilderPath(), {
        state: {
          from: detailsPath,
          returnTo: backTarget,
        },
      });
    } catch (_error) {
      editInFlightRef.current = false;
      setIsOpeningDraft(false);
      setActionError("We couldn't open the program for editing.");
    }
  }, [
    backTarget,
    beginHydrationTarget,
    generationResult,
    hydrateProgramDraft,
    navigate,
  ]);

  const handleAvailabilityChange = useCallback((field, value) => {
    setDraftAvailability((current) => {
      const next = {
        ...(current || {}),
        [field]: value,
      };

      if (field === "sessionsPerWeek") {
        next.preferredTrainingDays = adjustTrainingDaysForSessions(
          current?.preferredTrainingDays,
          value,
          trainingDaysTouchedRef.current
        );
      }

      draftAvailabilityRef.current = next;
      return next;
    });
    setGenerationError(null);
    setGenerationResult(null);
  }, []);

  const handleTrainingDayToggle = useCallback((day) => {
    trainingDaysTouchedRef.current = true;
    setDraftAvailability((current) => {
      const currentDays = normalizeTrainingDays(current?.preferredTrainingDays);
      const isSelected = currentDays.includes(day);
      const sessionsPerWeek = Number(current?.sessionsPerWeek);

      if (!isSelected && currentDays.length >= sessionsPerWeek) {
        return current;
      }

      const next = {
        ...(current || {}),
        preferredTrainingDays: normalizeTrainingDays(
          isSelected
            ? currentDays.filter((selectedDay) => selectedDay !== day)
            : [...currentDays, day]
        ),
      };
      draftAvailabilityRef.current = next;
      return next;
    });
    setGenerationError(null);
    setGenerationResult(null);
  }, []);

  const closeSettings = useCallback(() => {
    setIsSettingsOpen(false);
    loadSettings({ preserveDraft: true });
  }, [loadSettings]);

  const retrySettingsLoad = useCallback(() => {
    loadSettings({
      preserveDraft: Boolean(
        draftAvailabilityRef.current && savedAvailabilityRef.current
      ),
    });
  }, [loadSettings]);

  const openSettings = useCallback(() => {
    setIsSettingsOpen(true);
  }, []);

  // Chips describe what is actually being generated: the server-truth profile
  // with the availability this generation was launched with.
  const generationProfile = useMemo(() => {
    const profile = settingsState.data?.trainingProfile?.profile || {};
    return {
      ...profile,
      availability: {
        ...(profile.availability || {}),
        ...(draftAvailability || {}),
      },
    };
  }, [draftAvailability, settingsState.data]);

  if (!isEnabled) {
    return (
      <DesignV2Scope className="lz-ai-weekly" data-testid="ai-weekly-builder-root">
        <StackHeader
          title="AI WEEKLY PLAN BUILDER"
          backLabel="Back"
          onBack={() => navigate(backTarget)}
        />
        <main className="lz-ai-weekly__content">
          <Card>
            <h2 className="lz-ai-weekly__state-title">AI Builder unavailable</h2>
            <p className="lz-ai-weekly__muted-copy">
              AI weekly plan generation is currently unavailable.
            </p>
          </Card>
        </main>
      </DesignV2Scope>
    );
  }

  if (submissionStage === "generating") {
    return (
      <DesignV2Scope
        className="lz-ai-weekly lz-ai-weekly--generation"
        data-testid="ai-weekly-builder-root"
      >
        <GenerationProgress
          stage="generating"
          percent={generationProgress.percent}
          message={generationProgress.message}
          profile={generationProfile}
          isExiting={generationProgress.isCompletionExiting}
        />
      </DesignV2Scope>
    );
  }

  const showConfiguration =
    submissionStage === null && !generationResult && !generationError;

  return (
    <DesignV2Scope className="lz-ai-weekly" data-testid="ai-weekly-builder-root">
      <StackHeader
        title="AI WEEKLY PLAN BUILDER"
        backLabel="Back"
        onBack={() => navigate(backTarget)}
      />

      <main className="lz-ai-weekly__content">
        <div className="lz-ai-weekly__intro">
          <p>Generate a weekly workout plan built from your Training Profile.</p>
          <p>Adjust your availability before generating.</p>
        </div>

        <section aria-labelledby="training-profile-summary-title">
          <ProfileSummary
            settingsState={settingsState}
            onRetryLoad={retrySettingsLoad}
            onOpenSettings={openSettings}
          />
        </section>

        <section
          className="lz-ai-weekly__availability"
          aria-labelledby="adjust-availability-title"
        >
          <div className="lz-ai-weekly__section-heading">
            <h2 id="adjust-availability-title">Adjust availability</h2>
            <p>Adjust these for the program you want to generate now.</p>
          </div>

          <AvailabilityControl
            label="Sessions per week"
            description="Target frequency"
            value={draftAvailability?.sessionsPerWeek}
            allowedValues={sessionsValues}
            onChange={(value) => handleAvailabilityChange("sessionsPerWeek", value)}
            disabled={availabilityControlsDisabled}
          />

          <Card className="lz-ai-weekly__availability-card lz-ai-weekly__days-card">
            <div className="lz-ai-weekly__days-heading">
              <h3>Preferred training days</h3>
              <p>
                {normalizeTrainingDays(draftAvailability?.preferredTrainingDays).length} selected
              </p>
            </div>
            <WeekdaySelector
              options={DAY_OF_WEEK_OPTIONS}
              selectedValues={normalizeTrainingDays(
                draftAvailability?.preferredTrainingDays
              )}
              onToggle={handleTrainingDayToggle}
              maxSelected={Number(draftAvailability?.sessionsPerWeek)}
              disabled={availabilityControlsDisabled}
            />
          </Card>

          <AvailabilityControl
            label="Duration per session"
            description="Workout window"
            value={draftAvailability?.durationPerSession}
            allowedValues={durationValues}
            onChange={(value) => handleAvailabilityChange("durationPerSession", value)}
            disabled={availabilityControlsDisabled}
            unit="Min"
          />
        </section>

        {submissionStage === "saving" ? (
          <AvailabilitySavingState />
        ) : generationResult ? (
          <GeneratedProgramResult
            mode="weekly-plan"
            weeklyPlan={generationResult}
            profile={settingsState.data?.trainingProfile?.profile}
            onModify={handleModify}
            onDetails={handleSeeDetails}
            isModifyPending={isOpeningDraft}
            actionError={actionError}
          />
        ) : generationError ? (
          <Card as="section" className="lz-ai-weekly__error-card" role="alert">
            <div>
              <h2 className="lz-ai-weekly__state-title">
                {generationError.kind === "save"
                  ? "We couldn't save your availability"
                  : generationError.kind === "limit"
                    ? "Daily generation limit reached"
                    : "Generation interrupted"}
              </h2>
              <p className="lz-ai-weekly__muted-copy">{generationError.message}</p>
              {generationError.availabilityWasSaved ? (
                <p className="lz-ai-weekly__saved-copy">
                  Your availability was saved to your Training Profile. You can
                  retry generation without saving it again.
                </p>
              ) : null}
            </div>

            {generationError.kind === "profile" ? (
              <Button onClick={openSettings}>Review Training Profile</Button>
            ) : null}

            {RETRY_ERROR_KINDS.includes(generationError.kind) ? (
              <Button onClick={handleGenerate}>Try again</Button>
            ) : null}

            {generationError.kind === "network" ? (
              <div className="lz-ai-weekly__error-actions">
                <Button onClick={() => navigate(getWeeklyPlansPath())}>
                  Check my programs
                </Button>
                <Button variant="secondary" onClick={() => navigate(backTarget)}>
                  Back
                </Button>
              </div>
            ) : null}
          </Card>
        ) : null}
      </main>

      {showConfiguration ? (
        <StickyBottomActions
          className="lz-ai-weekly__actions"
          innerClassName="lz-ai-weekly__actions-inner"
        >
          <p className="lz-ai-weekly__persistence-note">
            Availability changes will update your Training Profile when generation starts.
          </p>
          <Button
            className="lz-ai-weekly__generate"
            onClick={handleGenerate}
            disabled={!canGenerate}
            aria-label="Generate my program"
          >
            Generate Program
          </Button>
        </StickyBottomActions>
      ) : null}

      <SettingsDrawer
        isOpen={isSettingsOpen}
        onClose={closeSettings}
        initialScreen={TRAINING_PROFILE_INITIAL_SCREEN}
      />
    </DesignV2Scope>
  );
}
